import { createBaseInputClass } from '@descope-ui/common/base-classes';
import {
  forwardAttrs,
  getComponentName,
  syncAttrs,
} from '@descope-ui/common/components-helpers';
import { asyncDebounce } from '@descope-ui/common/utils';

export const componentName = getComponentName('autocomplete-field-internal');

const BaseInputClass = createBaseInputClass({
  componentName,
  baseSelector: '',
});
const observedAttrs = [];

const INVALID_OPTION_VALUE = 'DESCOPE_INVALID_OPTION';
const DEBOUNCE_SEARCH_DELAY = 250;

class AutocompleteFieldInternal extends BaseInputClass {
  static get observedAttributes() {
    return [].concat(BaseInputClass.observedAttributes || [], observedAttrs);
  }

  get allowCustomValue() {
    return this.getAttribute('allow-custom-value') === 'true';
  }

  get minSearchLength() {
    return this.getAttribute('min-search-length') || 3;
  }

  get noResultsFoundMessage() {
    return this.getAttribute('no-results-message') || 'No results found';
  }

  get errorFetchingResultsMessage() {
    return (
      this.getAttribute('error-fetching-results-message') ||
      'An error occurred fetching results'
    );
  }

  constructor() {
    super();

    this.innerHTML = `
    <div class="autocomplete-field">
      <descope-combo-box clear-button-visible="true" auto-open-disabled="true" has-dynamic-data="true" item-label-path="data-name" item-value-path="data-id" hide-toggle-button="true"></descope-combo-box>
    </div>
    `;

    this.comboBox = this.querySelector('descope-combo-box');
  }

  get value() {
    return this.comboBox.value;
  }

  set value(val) {
    if (!this.allowCustomValue) {
      this.comboBox.data = [{ label: val, value: val }];
      setTimeout(() => {
        this.comboBox.value = val;
      });
    } else {
      this.comboBox.value = val;
    }
  }

  get defaultValue() {
    return this.getAttribute('default-value');
  }

  async #autoCompleteSearch(value) {
    const { results, error } = await this.fetchResults(value);
    if (error) {
      return { error };
    }
    return {
      results,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  #itemRenderer(displayName, value, label, disabled) {
    return `<span data-name="${label}" data-id="${value}" ${disabled ? 'disabled="true"' : ''}>${
      displayName || label
    }</span>`;
  }

  #overrideComboBoxRenderers() {
    this.comboBox.renderer = (root, _, model) => {
      if (model.item.getAttribute('disabled') === 'true') {
        root.setAttribute('disabled', 'true');
      } else {
        root.removeAttribute('disabled');
      }
      // eslint-disable-next-line no-param-reassign
      root.innerHTML = model.item.outerHTML;
    };

    this.comboBox.renderItem = ({ displayName, value, label, disabled }) =>
      this.#itemRenderer(displayName, value, label, disabled);
  }

  focus() {
    this.comboBox.focus();
  }

  init() {
    // This event listener needs to be placed before the super.init() call
    this.addEventListener('focus', (e) => {
      // we want to ignore focus events we are dispatching
      if (e.isTrusted) this.comboBox.focus();
    });

    super.init?.();
    this.#initComboBox();
    // This is required since when we remove the invalid attribute from the internal mappings field,
    // we want to reflect the change in the parent component
    syncAttrs(this, this.comboBox, { includeAttrs: ['invalid'] });
  }

  #initComboBox() {
    this.debouncedSearch = asyncDebounce(async (value) => {
      try {
        const { results, error } = await this.#autoCompleteSearch(value);
        return { results, error, value };
      } catch (error) {
        return { error, value };
      }
    }, DEBOUNCE_SEARCH_DELAY);

    forwardAttrs(this, this.comboBox, {
      includeAttrs: [
        'size',
        'bordered',
        'label',
        'label-type',
        'placeholder',
        'disabled',
        'readonly',
        'required',
        'full-width',
        'helper-text',
        'invalid',
        'error-message',
        'data-errormessage-value-missing',
        'st-host-direction',
        'allow-custom-value',
        'st-error-message-icon',
        'st-error-message-icon-size',
        'st-error-message-icon-padding',
      ],
    });

    this.comboBox.addEventListener('filter-changed', this.onSearch.bind(this));
    this.#overrideComboBoxRenderers();
  }

  #getEmptyResultsData() {
    return [
      {
        label: this.noResultsFoundMessage,
        value: INVALID_OPTION_VALUE,
        disabled: true,
      },
    ];
  }

  #getErrorResultsData() {
    return [
      {
        label: this.errorFetchingResultsMessage,
        value: INVALID_OPTION_VALUE,
        disabled: true,
      },
    ];
  }

  async onSearch(e) {
    const searchValue = e.detail.value;
    if (!searchValue || searchValue.length < this.minSearchLength) {
      return;
    }
    this.comboBox.loading = true;
    this.comboBox.setAttribute('opened', 'true');
    const response = await this.debouncedSearch(searchValue);
    if (response && response.value === searchValue) {
      this.comboBox.loading = false;
      if (response.error) {
        this.comboBox.data = this.#getErrorResultsData();
      } else if (response.results?.length === 0) {
        this.comboBox.data = this.#getEmptyResultsData();
      } else {
        this.comboBox.data = response.results || [];
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this,no-unused-vars
  fetchResults(value) {
    // eslint-disable-next-line no-console
    console.warn('fetchResults', 'needs to be implemented');
    return { results: [], error: undefined };
  }

  getValidity() {
    if (this.isRequired && !this.value) {
      return { valueMissing: true };
    }
    return { valid: true };
  }
}

export default AutocompleteFieldInternal;
